import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Table,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import {
  createAnnualBudget,
  getAnnualBudgets,
} from "../../redux/capacitacionDesarrollo/BudgetControl/actions";
import { getResponsables } from "../../redux/climalaboral/campaña/action";

const months = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const BudgetModal = ({ show, onHide, organizations }) => {
  //Estados
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const [organizacion, setOrganizacion] = useState("");
  const [año, setAño] = useState(currentYear);
  const [monto_total, setMontoTotal] = useState("");
  const [meses, setMeses] = useState(
    Array(12)
      .fill(null)
      .map((_, i) => ({ mes: i + 1, monto_asignado: "" }))
  );
  const [errors, setErrors] = useState({});
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  //Store
  const { responsable, responsableLoading } = useSelector(
    (store) => store.campaña
  );

  // Obtiene responsables de capacitacion
  useEffect(() => {
    dispatch(getResponsables({ rol_responsable: "Responsable_Capacitacion" }));
  }, [dispatch]);

  // Filtra empleados basado en búsqueda
  const empleadosFiltrados = useMemo(() => {
    if (!responsable) return [];
    return responsable.filter((emp) =>
      `${emp.first_name} ${emp.last_name} ${emp.username}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [responsable, searchTerm]);

  // Maneja selección/deselección de empleados
  const toggleEmpleadoSeleccionado = (empleado) => {
    setEmpleadosSeleccionados((prev) =>
      prev.some((e) => e.id === empleado.id)
        ? prev.filter((e) => e.id !== empleado.id)
        : [...prev, empleado]
    );
  };

  const resetForm = () => {
    setOrganizacion("");
    setAño(currentYear);
    setMontoTotal("");
    setMeses(
      Array(12)
        .fill(null)
        .map((_, i) => ({ mes: i + 1, monto_asignado: "" }))
    );
    setErrors({});
    setTotalAllocated(0);
    setEmpleadosSeleccionados([]);
    setSearchTerm("");
  };

  // Años disponibles
  const availableYears = [currentYear, currentYear + 1];

  // Calculos de porcentajes
  const calculatePercentage = (amount) => {
    if (!monto_total || monto_total <= 0) return 0;
    return ((amount / monto_total) * 100).toFixed(2);
  };

  const calculateTotalAllocated = (budgets) => {
    const total = budgets.reduce((sum, month) => {
      const amount = Number(month.monto_asignado) || 0;
      return sum + amount;
    }, 0);
    setTotalAllocated(total);
    return total;
  };

  //Validacion del formulario
  const validateForm = () => {
    const newErrors = {};

    if (!organizacion) newErrors.organizacion = "Seleccione una organización";
    if (!año || !availableYears.includes(año)) {
      newErrors.año = `Solo se permite planificar para ${currentYear} o ${
        currentYear + 1
      }`;
    }
    if (!monto_total || monto_total <= 0) {
      newErrors.monto_total = "Ingrese un monto válido";
    }

    const total = calculateTotalAllocated(meses);
    if (total !== Number(monto_total)) {
      newErrors.meses =
        "La suma mensual debe ser exactamente igual al monto total";
    }

    if (empleadosSeleccionados.length === 0) {
      newErrors.empleados =
        "Seleccione al menos un responsable de capacitación";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const distributeEvenly = () => {
    if (!monto_total || monto_total <= 0) return;

    const baseAmount = Math.floor(Number(monto_total) / 12);
    const remainder = Number(monto_total) % 12;

    const newMeses = meses.map((month, index) => ({
      ...month,
      monto_asignado: (index < remainder
        ? baseAmount + 1
        : baseAmount
      ).toString(),
    }));

    setMeses(newMeses);
    calculateTotalAllocated(newMeses);
  };

  // Manjeadores de eventos
  const handleMonthlyChange = (monthIndex, value) => {
    const newMeses = [...meses];
    newMeses[monthIndex].monto_asignado = value === "" ? "" : value;
    setMeses(newMeses);
    calculateTotalAllocated(newMeses);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const selectedOrganization = organizations.find(
      (org) => org.id === Number(organizacion)
    );
    const orgName = selectedOrganization ? selectedOrganization.name : "";

    const budgetData = {
      organizacion: Number(organizacion),
      año: año,
      monto_total: Number(monto_total),
      meses: meses.map((m) => ({
        mes: m.mes,
        monto_asignado: Number(m.monto_asignado) || 0,
      })),
      responsables: empleadosSeleccionados.map((e) => e.username),
    };

    dispatch(createAnnualBudget(budgetData, orgName))
      .then(() => {
        const params = {
          organizacion: organizacion,
          anio: año,
        };
        return dispatch(getAnnualBudgets(params));
      })
      .then(() => {
        handleClose();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="border-bottom-0">
        <Modal.Title style={{ fontSize: "1.4rem" }}>
          Nuevo Presupuesto Anual
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0" style={{ fontSize: "1rem" }}>
        {errors.meses && (
          <Alert
            variant="danger"
            className="mb-3 py-2"
            style={{ fontSize: "1rem" }}
          >
            {errors.meses}
          </Alert>
        )}

        <Form>
          <Row className="mb-3 g-2">
            <Col md={6}>
              <Form.Group controlId="formOrganizacion">
                <Form.Label className="mb-1" style={{ fontSize: "1rem" }}>
                  Organización
                </Form.Label>
                <Form.Control
                  as="select"
                  value={organizacion}
                  onChange={(e) => setOrganizacion(e.target.value)}
                  isInvalid={!!errors.organizacion}
                  style={{ fontSize: "1rem" }}
                  disabled={isSubmitting}
                >
                  <option value="">Seleccione una organización</option>
                  {organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.95rem" }}
                >
                  {errors.organizacion}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 g-2">
            <Col md={6}>
              <Form.Group controlId="formAño">
                <Form.Label className="mb-1" style={{ fontSize: "1rem" }}>
                  Año
                </Form.Label>
                <Form.Control
                  as="select"
                  value={año}
                  onChange={(e) => setAño(parseInt(e.target.value))}
                  isInvalid={!!errors.año}
                  style={{ fontSize: "1rem" }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.95rem" }}
                >
                  {errors.año}
                </Form.Control.Feedback>
                <Form.Text style={{ fontSize: "0.95rem" }}>
                  Planificación para {currentYear} o {currentYear + 1}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formMontoTotal">
                <Form.Label className="mb-1" style={{ fontSize: "1rem" }}>
                  Monto Total
                </Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="number"
                    value={monto_total || ""}
                    onChange={(e) => setMontoTotal(Number(e.target.value) || 0)}
                    isInvalid={!!errors.monto_total}
                    min="0"
                    step="1"
                    style={{ fontSize: "1rem" }}
                  />
                  <Button
                    variant="outline-primary"
                    className="ms-2 px-3"
                    onClick={distributeEvenly}
                    disabled={!monto_total || monto_total <= 0 || isSubmitting}
                    style={{ fontSize: "1rem" }}
                  >
                    Distribuir
                  </Button>
                </div>
                <Form.Control.Feedback
                  type="invalid"
                  style={{ fontSize: "0.95rem" }}
                >
                  {errors.monto_total}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3 g-2">
            <Col md={12}>
              <Form.Group controlId="formResponsables">
                <Form.Label className="mb-1" style={{ fontSize: "1rem" }}>
                  Responsables de Capacitación / Notificación (
                  {empleadosSeleccionados.length} seleccionados)
                </Form.Label>
                <InputGroup className="mb-3">
                  <FormControl
                    placeholder="Buscar empleados..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ fontSize: "1rem" }}
                  />
                </InputGroup>

                {errors.empleados && (
                  <Alert
                    variant="danger"
                    className="mb-3 py-2"
                    style={{ fontSize: "1rem" }}
                  >
                    {errors.empleados}
                  </Alert>
                )}
                <div
                  style={{
                    maxHeight: "100px",
                    overflowY: "auto",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    padding: "10px",
                  }}
                >
                  {responsableLoading ? (
                    <div className="text-center py-3">
                      Cargando empleados...
                    </div>
                  ) : empleadosFiltrados.length === 0 ? (
                    <div className="text-center py-3">
                      No se encontraron empleados
                    </div>
                  ) : (
                    empleadosFiltrados.map((empleado) => (
                      <Form.Check
                        key={empleado.id}
                        type="checkbox"
                        id={`emp-${empleado.id}`}
                        label={`${empleado.first_name} ${empleado.last_name}`}
                        checked={empleadosSeleccionados.some(
                          (e) => e.id === empleado.id
                        )}
                        onChange={() => toggleEmpleadoSeleccionado(empleado)}
                        className="mb-2"
                      />
                    ))
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 style={{ fontSize: "1.1rem", fontWeight: "600" }}>
                Distribución Mensual
              </h6>
              <div className="text-nowrap" style={{ fontSize: "1rem" }}>
                <span style={{ fontWeight: "500" }}>
                  Total: L{totalAllocated.toLocaleString("en-US")}
                </span>{" "}
                |
                <span
                  className={
                    (Number(monto_total) || 0) - totalAllocated < 0
                      ? "text-danger ms-1"
                      : "text-success ms-1"
                  }
                >
                  Resto: L
                  {((Number(monto_total) || 0) - totalAllocated).toLocaleString(
                    "en-US"
                  )}
                </span>
              </div>
            </div>

            <div
              className="border rounded"
              style={{ maxHeight: "350px", overflowY: "auto" }}
            >
              <Table striped bordered hover className="mb-0">
                <thead className="sticky-top bg-light">
                  <tr>
                    <th style={{ width: "25%", fontSize: "1rem" }}>Mes</th>
                    <th style={{ width: "25%", fontSize: "1rem" }}>
                      Monto Asignado
                    </th>
                    <th style={{ width: "20%", fontSize: "1rem" }}>
                      % del Total
                    </th>
                    <th style={{ width: "30%", fontSize: "1rem" }}>Progreso</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((month, index) => {
                    const percentage = calculatePercentage(
                      meses[index]?.monto_asignado || 0
                    );
                    return (
                      <tr key={index}>
                        <td
                          className="align-middle"
                          style={{ fontSize: "1rem", fontWeight: "500" }}
                        >
                          {month}
                        </td>
                        <td className="p-1">
                          <Form.Control
                            type="number"
                            value={meses[index]?.monto_asignado || ""}
                            onChange={(e) =>
                              handleMonthlyChange(index, e.target.value)
                            }
                            min="0"
                            step="1"
                            className="border-0 py-1 px-2"
                            style={{
                              boxShadow: "none",
                              height: "35px",
                              fontSize: "1rem",
                            }}
                          />
                        </td>
                        <td
                          className="align-middle text-center"
                          style={{ fontSize: "1rem" }}
                        >
                          {percentage}%
                        </td>
                        <td className="align-middle">
                          <div className="progress" style={{ height: "25px" }}>
                            <div
                              className={`progress-bar ${
                                percentage > 8.33 ? "bg-warning" : "bg-success"
                              }`}
                              role="progressbar"
                              style={{ width: `${percentage}%` }}
                              aria-valuenow={percentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            >
                              {percentage > 10 && (
                                <span style={{ fontSize: "0.9rem" }}>
                                  {percentage}%
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          style={{ fontSize: "1rem", padding: "0.375rem 1rem" }}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{ fontSize: "1rem", padding: "0.375rem 1.5rem" }}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span className="ms-2">&nbsp;Guardando...</span>
            </>
          ) : (
            "Guardar Presupuesto"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BudgetModal;
